import { useCallback, useEffect, useState } from 'react';

export const MIN_COLUMN_WIDTH = 60;
export const MAX_COLUMN_WIDTH = 900;

type Overrides<K extends string> = Partial<Record<K, number>>;

export function clampColumnWidth(width: number): number {
  return Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, Math.round(width)));
}

function readOverrides<K extends string>(
  storageKey: string,
  defaults: Record<K, number>,
): Overrides<K> {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};

    const overrides: Overrides<K> = {};
    for (const key of Object.keys(defaults) as K[]) {
      const value = (parsed as Record<string, unknown>)[key];
      if (typeof value === 'number' && Number.isFinite(value)) {
        overrides[key] = clampColumnWidth(value);
      }
    }
    return overrides;
  } catch {
    return {};
  }
}

function writeOverrides(storageKey: string, overrides: object): void {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(overrides));
  } catch {
    // Storage unavailable (private mode, quota) - widths then last for the session only.
  }
}

/**
 * Per-column widths for a resizable table. Only the widths the merchant
 * actually changed are persisted under `storageKey`, so untouched columns
 * keep following `defaults` when those change in a later release.
 */
export function useColumnWidths<K extends string>(storageKey: string, defaults: Record<K, number>) {
  const [overrides, setOverrides] = useState<Overrides<K>>(() =>
    readOverrides(storageKey, defaults),
  );

  useEffect(() => {
    writeOverrides(storageKey, overrides);
  }, [storageKey, overrides]);

  const setWidth = useCallback((key: K, width: number) => {
    setOverrides((current) => ({ ...current, [key]: clampColumnWidth(width) }));
  }, []);

  const resetWidth = useCallback((key: K) => {
    setOverrides((current) => {
      if (!(key in current)) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  }, []);

  const widths = { ...defaults, ...overrides } as Record<K, number>;

  return { widths, setWidth, resetWidth };
}
